<template>
  <div>
    <es-filter :defaultExpanded="true">
      <template #filter-options>
        <div class="columns multi-dropdown-columns">
          <div class="column is-2">
            <div class="field">
              <label class="label has-text-weight-bold">
                Batch ID:
              </label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="batchIdInput"
                  :aria-label="batchIdInput || 'Batch ID'"
                />
              </div>
            </div>
          </div>
          <div class="column is-2">
            <es-select
              key-value="code"
              label="Submission Type : "
              ref="selSubmissionType"
              value-tpl="${description}"
              :clearOut="true"
              :multi="true"
              :set-values="activeSubmissionTypes"
              @update:values="submissionTypes = $event"
            />
          </div>
          <div class="column is-2">
            <es-select
              key-value="id"
              ref="selYears"
              value-tpl="${year}"
              :clearOut="true"
              :label="`${getFieldLabel(`PER:${personType}:ASSIGN:SCHOOL_YEAR`)} :`"
              :multi="true"
              :pre-selected="schoolYearInput"
              :set-values="pidSchoolYearsDesc"
              @update:values="schoolYearInput = $event"
            />
          </div>
          <div class="column is-4">
            <div class="field no-border">
              <label class="label has-text-weight-bold">
                Process Date Range :
              </label>
              <div class="control is-flex">
                <input
                  class="input"
                  type="date"
                  v-model="startDateInput"
                  :aria-label="startDateInput || 'Start Date Input'"
                  >
                <span
                  class="is-flex flex-align--center"
                  style="padding: 0 0.5rem; font-size: 12px"
                >
                  to
                </span>
                <input
                  class="input"
                  type="date"
                  v-model="endDateInput"
                  :aria-label="endDateInput || 'End Date Input'"
                >
              </div>
            </div>
          </div>
          <div
            class="column has-padding-right"
            style="margin-top: auto"
          >
            <div class="buttons flex-justify--end">
              <button
                class="button button-link has-underline"
                @click="resetFilter"
              >
                Reset
              </button>
              <button
                class="button is-primary"
                :disabled="disableFilter"
                :title="disableFilter
                  ? 'Batch ID, or Start Date is required.'
                  : null"
                @click="filterData"
              >
                Filter Data
              </button>
            </div>
          </div>
        </div>
      </template>
    </es-filter>
    <div style="padding: 0 1.25rem .5rem 2rem">
      <es-exception v-if="isStateUser && !schoolYear.length"
        style="padding: 6.5rem" margin="0rem"
        exceptionMessage="School Year must be entered to view results."
      />
      <pid-batches-table
        v-else
        :batches="rejectedBatches"
        :columnHeaders="columnHeaders"
        :loading="loadingBatches"
        :loadingError="loadingBatchesError"
        :pageSize="pageSize"
        :totalCount="rejectedBatchesTotalCount"
        @updateSorter="updateSorter"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { mapActions, mapGetters } from 'vuex';
import EsFilter from '@/components/EsFilter.vue';
import EsSelect from '@/components/EsSelect.vue';
import PidBatchesTable from '@/components/PidBatchesTable.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';

export default {
  mixins: [
    DateUtils,
    FieldLabels,
  ],
  components: {
    EsFilter,
    EsSelect,
    PidBatchesTable,
  },
  props: {
    batchId: {
      type: String,
    },
    endDate: {
      type: String,
    },
    page: {
      type: Number,
    },
    schoolYear: {
      type: Array,
    },
    sortField: {
      type: String,
      default: 'id',
    },
    sortOrder: {
      type: String,
      default: 'DESC',
    },
    startDate: {
      type: String,
    },
  },
  data() {
    return {
      loading: false,
      loadingError: false,

      // props and filter
      endDateInput: this.endDate,
      batchIdInput: this.batchId,
      schoolYearInput: [],
      startDateInput: this.startDate,
      submissionTypes: [],

      // data for batch view
      columnHeaders: [],
      loadingBatches: false,
      loadingBatchesError: null,
      pageSize: 25,
      rejectedBatches: [],
      rejectedBatchesTotalCount: null,
      userPersonTypesCodes: null,
    };
  },
  async created() {
    this.onLoad();
    this.setDataElements();
    if (!this.isStateUser || this.schoolYear.length) {
      this.loadRejectedBatches();
    }
    this.userPersonTypesCodes = this.userPersonTypes
      .map(item => item.code);
  },
  mounted() {
    this.$root.$on('refreshMatchHistory', this.loadRejectedBatches);
  },
  destroyed() {
    this.$root.$off('refreshMatchHistory');
  },
  watch: {
    $route() {
      if (!this.isStateUser || this.schoolYear.length) {
        this.loadRejectedBatches();
      }
    },
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters({
      isStateUser: 'isStateUser',
    }),
    ...mapGetters('lookups', [
      'activeSubmissionTypes',
      'defaultPersonType',
      'pidSchoolYearsDesc',
    ]),
    disableFilter() {
      return this.loadingBatches
        || (this.isStateUser && (!this.schoolYearInput || !this.schoolYearInput.length))
        || (this.isStateUser
        && !this.startDateInput
        && !this.batchIdInput);
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    hasInputParams() {
      return (
        this.batchIdInput
        || this.endDateInput
        || this.page
        || this.submissionTypes.length
        || this.startDateInput
        || this.schoolYearInput.length > 0
      );
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
    searchParams() {
      const params = {};
      const trimmedBatchId = this.batchIdInput ? this.batchIdInput.trim() : '';
      if (trimmedBatchId) {
        params.batchId = trimmedBatchId;
      }
      if (this.submissionTypes) {
        params.submissionTypes = this.submissionTypes.map(c => c.code);
      }
      if (this.schoolYearInput.length) {
        params.schoolYear = this.schoolYearInput.map(y => y.year);
      }
      if (this.startDateInput) {
        params.startDate = this.startDateInput;
      }
      if (this.endDateInput) {
        params.endDate = this.endDateInput;
      }
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    urlParams() {
      const params = {};

      params.page = this.page ? this.page : undefined;
      params.pageSize = this.pageSize ? this.pageSize : undefined;
      params.ids = this.batchIdInput ? this.batchIdInput : undefined;
      params.schoolYear = this.schoolYear.length ? this.schoolYear : this.$route.query.schoolYear;
      params.startDate = this.startDate ? this.startDate : this.$route.query.startDate;
      params.submissionTypes = this.submissionTypes.length
        ? this.submissionTypes.map(c => c.code) : this.$route.query.submissionTypes;
      params.endDate = this.endDate ? this.endDate : this.$route.query.endDate;
      params.sort = JSON.stringify([{
        field: this.$route.query.sortField || this.sortField,
        order: this.$route.query.sortOrder || this.sortOrder,
      }]);
      return params;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadSubmissionTypes',
    ]),
    filterData() {
      // Always trim batchIdInput before filtering
      if (typeof this.batchIdInput === 'string') {
        this.batchIdInput = this.batchIdInput.trim();
      }
      if (!this.isStateUser || this.hasInputParams) {
        const params = { ...this.searchParams };
        if (!this.deepEqual(this.$route.query, params)) {
          this.$router.replace({ query: params });
        } else {
          this.loadRejectedBatches();
        }
      } else if (!this.hasInputParams && this.searchParams) {
        this.resetFilter();
      }
    },
    deepEqual(a, b) {
      if (a === b) return true;
      if (typeof a !== typeof b) return false;
      if (typeof a !== 'object' || a === null || b === null) return false;
      if (Array.isArray(a) !== Array.isArray(b)) return false;

      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      if (aKeys.length !== bKeys.length) return false;

      return aKeys.every(key => bKeys.includes(key) && this.deepEqual(a[key], b[key]));
    },
    getRejectedBatches() {
      const payLoad = {
        params: this.urlParams,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
        paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
      };
      return axios.get('personid/rejected', payLoad);
    },
    async loadRejectedBatches() {
      try {
        this.$root.$emit('matchHistoryLoading', true);
        this.loadingBatches = true;
        this.loadingBatchesError = false;

        const response = await this.getRejectedBatches();
        if (response.status === 204) {
          this.rejectedBatches = [];
          this.rejectedBatchesTotalCount = null;
        } else {
          this.rejectedBatches = response.data.data;
          this.rejectedBatchesTotalCount = response.data.totalCount;
        }
      } catch (e) {
        this.loadingBatchesError = true;
      } finally {
        this.$root.$emit('matchHistoryLoading', false);
        this.loadingBatches = false;
      }
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        await Promise.all([
          this.loadSubmissionTypes(),
        ]);
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    resetFilter() {
      // TODO: searchParams() returns all keys (even if the value is undefined)
      if (Object.keys(this.urlParams).length) {
        this.$router.replace({ query: {} });
      }

      this.$refs.selYears.clear();
      this.$refs.selYears.pushValues(
        this.pidSchoolYearsDesc.filter(
          y => this.$store.state.propertiesModule.properties['RULE-CURRENT_SCHOOL_YEAR'] === y.year,
        ),
      );
      this.$refs.selSubmissionType.clear();

      this.batchIdInput = null;
      this.startDateInput = this.buildDateString(29);
      this.endDateInput = null;

      this.rejectedBatches = [];
      this.rejectedBatchesTotalCount = null;

      this.filterData();
    },
    setDataElements() {
      this.schoolYearInput = this.pidSchoolYearsDesc
        .filter(({ year }) => this.schoolYear.includes(year));
      this.columnHeaders = [
        {
          header: null,
          field: null,
          style: {
            width: '55px',
          },
        },
        {
          header: 'Batch Info',
          title: 'Batch Info',
          field: 'id',
          order: 'DESC',
          sortable: true,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Location Info',
          title: 'Location Info',
          field: null,
          style: {
            width: '30%',
          },
          hideForRejectedBatch: true,
        },
        {
          header: 'Process Info',
          title: 'Process Info',
          field: 'createdDate',
          order: null,
          sortable: true,
          style: {
            idth: '10%',
          },
        },
        {
          header: 'Record Info',
          title: 'Record Info',
          field: null,
          style: {
            width: '10%',
          },
          hideForRejectedBatch: true,
        },
        {
          header: 'Creation User',
          title: 'Creation User',
          field: 'email',
          sortable: true,
          order: null,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Status Info',
          title: 'Status Info',
          field: null,
        },
        {
          header: 'Actions',
          title: 'Actions',
          field: null,
          style: {
            width: '1%',
          },
        },
      ];
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
